import { collectPeopleFromCrash, getPersonKey, getPersonNameAndSurname } from './match-display-rows'

const MIN_MATCH_SCORE = 8
const MAX_MATCH_SCORE = 11
const MAX_LEVENSHTEIN_DISTANCE = 3

/**
 * @param {string | null | undefined} input
 */
function normalizeString(input) {
  if (input == null) return ''
  return String(input)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * @param {string} s1
 * @param {string} s2
 */
function normalizedLevenshtein(s1, s2) {
  const a = normalizeString(s1)
  const b = normalizeString(s2)
  const n = a.length
  const m = b.length
  if (n === 0) return m
  if (m === 0) return n

  const prev = new Array(m + 1)
  const curr = new Array(m + 1)
  for (let j = 0; j <= m; j++) prev[j] = j

  for (let i = 1; i <= n; i++) {
    curr[0] = i
    for (let j = 1; j <= m; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost)
    }
    for (let j = 0; j <= m; j++) prev[j] = curr[j]
  }
  return prev[m]
}

/**
 * @param {string | null | undefined} left
 * @param {string | null | undefined} right
 */
function getLevenshteinScore(left, right) {
  if (!left || !right) return 0
  const distance = normalizedLevenshtein(left, right)
  if (distance <= MAX_LEVENSHTEIN_DISTANCE) return 5 - distance
  return 0
}

/**
 * @param {string | null | undefined} left
 * @param {string | null | undefined} right
 */
function scoreBestTokenPair(left, right) {
  if (!left?.trim() || !right?.trim()) return 0
  const leftTokens = left.trim().split(/\s+/).filter(Boolean)
  const rightTokens = right.trim().split(/\s+/).filter(Boolean)
  if (leftTokens.length === 0 || rightTokens.length === 0) return 0

  let best = 0
  for (const leftToken of leftTokens) {
    for (const rightToken of rightTokens) {
      const distance = normalizedLevenshtein(leftToken, rightToken)
      if (distance <= MAX_LEVENSHTEIN_DISTANCE) best = Math.max(best, 5 - distance)
    }
  }
  return best
}

/**
 * Punteggio allineato a PersonMatchScorer / MatchService.ComparePerson (soglia ≥ 8).
 * Richiede segnale diretto su nome e cognome; token bonus senza doppio conteggio.
 * @param {Record<string, unknown>} anchorPerson
 * @param {Record<string, unknown>} candidatePerson
 */
export function scorePersonMatch(anchorPerson, candidatePerson) {
  const anchor = getPersonNameAndSurname(anchorPerson)
  const candidate = getPersonNameAndSurname(candidatePerson)
  const anchorName = anchor.name
  const anchorSurname = anchor.surname
  const candidateName = candidate.name
  const candidateSurname = candidate.surname

  const nameScore = getLevenshteinScore(anchorName, candidateName)
  const surnameScore = getLevenshteinScore(anchorSurname, candidateSurname)
  const crossNameSurname = getLevenshteinScore(anchorName, candidateSurname)
  const crossSurnameName = getLevenshteinScore(anchorSurname, candidateName)

  const tokenScore = scoreBestTokenPair(
    `${anchorName} ${anchorSurname}`.trim(),
    `${candidateName} ${candidateSurname}`.trim(),
  )
  const coveredByDirect = Math.max(nameScore, surnameScore)
  const tokenBonus = Math.max(0, tokenScore - coveredByDirect)

  let personScore = nameScore + surnameScore + crossNameSurname + crossSurnameName + tokenBonus

  const anchorGender = anchorPerson?.Gender
  const candidateGender = candidatePerson?.Gender
  if (
    anchorGender != null &&
    anchorGender !== '' &&
    candidateGender != null &&
    candidateGender !== '' &&
    String(anchorGender) === String(candidateGender)
  ) {
    personScore += 1
  }

  const matchPercent = (personScore / MAX_MATCH_SCORE) * 100
  const isMatch =
    personScore >= MIN_MATCH_SCORE && nameScore > 0 && surnameScore > 0
  return { personScore, matchPercent, nameScore, surnameScore, isMatch }
}

/**
 * Percentuale mostrata in UI: mai sopra 100%; stellina se il punteggio reale è ≥ 100%.
 * @param {number} matchPercent
 */
export function getMatchPercentDisplay(matchPercent) {
  const raw = Number(matchPercent)
  const safe = Number.isFinite(raw) ? raw : 0
  return {
    displayPercent: Math.min(100, Math.max(0, safe)),
    showStar: safe >= 100,
  }
}

/**
 * @param {Record<string, unknown>} anchorPerson
 * @param {Record<string, unknown>} candidatePerson
 * @param {string} matchRole
 */
function buildProposal(anchorPerson, candidatePerson, matchRole) {
  const { personScore, matchPercent, isMatch } = scorePersonMatch(anchorPerson, candidatePerson)
  if (!isMatch) return null

  return {
    anchorPersonKey: getPersonKey(anchorPerson),
    matchPersonKey: getPersonKey(candidatePerson),
    matchRole,
    score: personScore,
    matchPercent,
    anchorPerson,
    matchPerson: candidatePerson,
  }
}

/**
 * @param {Record<string, unknown>[]} proposals
 */
export function detectCollisions(proposals) {
  /** @type {Map<string, typeof proposals>} */
  const byMatch = new Map()
  for (const proposal of proposals) {
    const key = `${proposal.matchRole}:${proposal.matchPersonKey}`
    if (!byMatch.has(key)) byMatch.set(key, [])
    byMatch.get(key).push(proposal)
  }

  return [...byMatch.entries()]
    .filter(([, items]) => items.length > 1)
    .map(([key, items]) => ({
      key,
      matchRole: items[0].matchRole,
      matchPersonKey: items[0].matchPersonKey,
      matchPerson: items[0].matchPerson,
      anchorCandidates: items.map((item) => ({
        anchorPersonKey: item.anchorPersonKey,
        anchorPerson: item.anchorPerson,
        score: item.score,
        matchPercent: item.matchPercent,
      })),
    }))
}

/**
 * Collisioni ancora aperte: più anchor disponibili per lo stesso candidato.
 * @param {Record<string, unknown>[]} proposals
 * @param {Record<string, string>} collisionChoices
 * @param {Set<string>} usedAnchors
 * @param {Set<string>} usedMatches
 */
export function getUnresolvedCollisions(
  proposals,
  collisionChoices = {},
  usedAnchors = new Set(),
  usedMatches = new Set(),
) {
  return detectCollisions(proposals).filter((collision) => {
    if (collisionChoices[collision.matchPersonKey]) return false

    const activeAnchors = collision.anchorCandidates.filter(
      (candidate) =>
        !usedAnchors.has(candidate.anchorPersonKey) &&
        !usedMatches.has(collision.matchPersonKey) &&
        proposals.some(
          (p) =>
            p.anchorPersonKey === candidate.anchorPersonKey &&
            p.matchPersonKey === collision.matchPersonKey,
        ),
    )

    return activeAnchors.length > 1
  })
}

/**
 * @param {Record<string, unknown>[]} proposals
 * @param {Record<string, string>} collisionChoices matchPersonKey → anchorPersonKey
 */
export function resolveConfirmedPairs(proposals, collisionChoices = {}) {
  const confirmed = []
  const usedAnchors = new Set()
  const usedMatches = new Set()

  for (const collision of detectCollisions(proposals)) {
    const anchorKey = collisionChoices[collision.matchPersonKey]
    if (!anchorKey) continue
    const picked = proposals.find(
      (p) =>
        p.matchPersonKey === collision.matchPersonKey &&
        p.anchorPersonKey === anchorKey,
    )
    if (!picked) continue
    confirmed.push(picked)
    usedAnchors.add(picked.anchorPersonKey)
    usedMatches.add(picked.matchPersonKey)
  }

  const sortedRemaining = [...proposals].sort((a, b) => b.score - a.score)

  while (true) {
    const unresolvedKeys = new Set(
      getUnresolvedCollisions(proposals, collisionChoices, usedAnchors, usedMatches)
        .map((c) => c.matchPersonKey),
    )

    const next = sortedRemaining.find(
      (proposal) =>
        !usedAnchors.has(proposal.anchorPersonKey) &&
        !usedMatches.has(proposal.matchPersonKey) &&
        !unresolvedKeys.has(proposal.matchPersonKey),
    )

    if (!next) break

    confirmed.push(next)
    usedAnchors.add(next.anchorPersonKey)
    usedMatches.add(next.matchPersonKey)
  }

  return confirmed.sort((a, b) => a.anchorPersonKey.localeCompare(b.anchorPersonKey))
}

/**
 * @param {Record<string, unknown>[]} people
 */
function uniquePeopleByKey(people) {
  const seen = new Set()
  return people.filter((person) => {
    const key = getPersonKey(person)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * @param {typeof proposals[number][]} proposals
 */
function dedupeProposals(proposals) {
  const seen = new Set()
  return proposals.filter((proposal) => {
    const key = `${proposal.matchRole}:${proposal.anchorPersonKey}:${proposal.matchPersonKey}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * @param {Record<string, unknown>} person
 * @param {string} candName
 * @param {string} candSurname
 */
function personMatchesCandidateNames(person, candName, candSurname) {
  const { name, surname } = getPersonNameAndSurname(person)
  return (
    normalizeString(name) === normalizeString(candName) &&
    normalizeString(surname) === normalizeString(candSurname)
  )
}

/**
 * Usa candidate_name / candidate_surname dall'API match (come il chip Match).
 * @param {Record<string, unknown>[]} anchorPeople
 * @param {Record<string, unknown>} matchDetail
 * @param {string} matchRole
 */
function buildProposalsFromApiMetas(anchorPeople, matchDetail, matchRole) {
  const metas = Array.isArray(matchDetail?._matchMetas) && matchDetail._matchMetas.length > 0
    ? matchDetail._matchMetas
    : (matchDetail?.candidate_name || matchDetail?.candidate_surname)
      ? [matchDetail]
      : []

  if (metas.length === 0) return []

  const candidatePeople = collectPeopleFromCrash(matchDetail)
  const proposals = []

  for (const meta of metas) {
    const candName = String(meta?.candidate_name ?? '').trim()
    const candSurname = String(meta?.candidate_surname ?? '').trim()
    if (!candName && !candSurname) continue

    const matchPerson = candidatePeople.find((person) =>
      personMatchesCandidateNames(person, candName, candSurname),
    )
    if (!matchPerson) continue

    const { unit: _matchUnit, ...matchOnly } = matchPerson

    for (const anchorPerson of anchorPeople) {
      const { unit, ...anchorOnly } = anchorPerson
      const proposal = buildProposal(anchorOnly, matchOnly, matchRole)
      if (proposal) proposals.push(proposal)
    }
  }

  return proposals
}

/**
 * @param {Record<string, unknown>[]} anchorPeople
 * @param {Record<string, unknown> | null | undefined} matchDetail
 * @param {string} matchRole
 * @param {Record<string, string>} [collisionChoices]
 */
export function buildMatchPairBindings(
  anchorPeople,
  matchDetail,
  matchRole,
  collisionChoices = {},
) {
  if (!matchDetail || !Array.isArray(anchorPeople) || anchorPeople.length === 0) {
    return { proposals: [], collisions: [], confirmed: [], unresolvedCollisions: [] }
  }

  const uniqueAnchors = uniquePeopleByKey(anchorPeople)
  const candidatePeople = uniquePeopleByKey(collectPeopleFromCrash(matchDetail))
  const fuzzyProposals = []

  for (const anchorPerson of uniqueAnchors) {
    const { unit, ...anchorOnly } = anchorPerson
    for (const candidatePerson of candidatePeople) {
      const { unit: _u, ...candidateOnly } = candidatePerson
      const proposal = buildProposal(anchorOnly, candidateOnly, matchRole)
      if (proposal) fuzzyProposals.push(proposal)
    }
  }

  const apiProposals = buildProposalsFromApiMetas(uniqueAnchors, matchDetail, matchRole)
  const proposals = dedupeProposals([...fuzzyProposals, ...apiProposals])

  const confirmed = resolveConfirmedPairs(proposals, collisionChoices)
  const usedAnchors = new Set(confirmed.map((p) => p.anchorPersonKey))
  const usedMatches = new Set(confirmed.map((p) => p.matchPersonKey))
  const unresolvedCollisions = getUnresolvedCollisions(
    proposals,
    collisionChoices,
    usedAnchors,
    usedMatches,
  )

  return { proposals, collisions: detectCollisions(proposals), confirmed, unresolvedCollisions }
}

/**
 * @param {Record<string, unknown> | null | undefined} anchorDetail
 * @param {Record<string, unknown>} crashByRole
 * @param {string} anchorRole
 * @param {Record<string, Record<string, string>>} collisionChoicesByRole
 */
export function getConfirmedMergePeople(
  anchorDetail,
  crashByRole,
  anchorRole,
  collisionChoicesByRole = {},
) {
  const anchorPeople = collectPeopleFromCrash(anchorDetail ?? {})
  /** @type {Map<string, { anchorPerson: Record<string, unknown>, matches: Record<string, Record<string, unknown>>, pairIndex: number }>} */
  const byAnchor = new Map()

  for (const role of ['police', 'hospital', 'insurance']) {
    if (role === anchorRole) continue
    const detail = crashByRole?.[role]
    if (!detail?.crash) continue

    const { confirmed } = buildMatchPairBindings(
      anchorPeople,
      detail,
      role,
      collisionChoicesByRole[role] ?? {},
    )

    for (const pair of confirmed) {
      if (!byAnchor.has(pair.anchorPersonKey)) {
        byAnchor.set(pair.anchorPersonKey, {
          anchorPerson: pair.anchorPerson,
          matches: {},
        })
      }
      byAnchor.get(pair.anchorPersonKey).matches[role] = pair.matchPerson
    }
  }

  return [...byAnchor.values()].filter((entry) => Object.keys(entry.matches).length > 0)
}

/**
 * @param {ReturnType<typeof getConfirmedMergePeople>} mergePeople
 */
export function getAnchorPeopleForMergeForms(mergePeople) {
  return mergePeople.map((entry) => entry.anchorPerson)
}

/**
 * @param {Record<string, unknown> | null | undefined} anchorDetail
 * @param {Record<string, unknown>} crashByRole
 * @param {string} anchorRole
 * @param {Record<string, Record<string, string>>} collisionChoicesByRole
 */
export function hasUnresolvedPairCollisions(
  anchorDetail,
  crashByRole,
  anchorRole,
  collisionChoicesByRole = {},
) {
  const anchorPeople = collectPeopleFromCrash(anchorDetail ?? {})

  for (const role of ['police', 'hospital', 'insurance']) {
    if (role === anchorRole) continue
    const detail = crashByRole?.[role]
    if (!detail?.crash) continue

    const { proposals, confirmed } = buildMatchPairBindings(
      anchorPeople,
      detail,
      role,
      collisionChoicesByRole[role] ?? {},
    )
    if (proposals.length === 0) continue

    const usedAnchors = new Set(confirmed.map((p) => p.anchorPersonKey))
    const usedMatches = new Set(confirmed.map((p) => p.matchPersonKey))
    const unresolved = getUnresolvedCollisions(
      proposals,
      collisionChoicesByRole[role] ?? {},
      usedAnchors,
      usedMatches,
    )
    if (unresolved.length > 0) return true
  }

  return false
}
